/**
 * AoETargetingOverlay - Visual area of effect targeting overlay
 * @date_of_change 2025-09-15 - AOE TARGETING SYSTEM
 *
 * Renders transparent area of effect shapes on the battle grid to help users
 * visualize spell targeting areas. Supports circles, squares, cones, and lines.
 * Handles both corner and center targeting with proper D&D 5e grid calculations.
 */

import React, { useMemo } from 'react';
import { Group, Circle, Rect, Line, Path } from 'react-konva';
import RangeCalculator from '../../../utils/RangeCalculator';
import { normalizeAreaOfEffect } from '../../../utils/areaOfEffectUtils';

const AoETargetingOverlay = ({
  targetPoint,
  areaOfEffect,
  gridSize = 50,
  feetPerCell = 5,
  gridType = 'square',
  isVisible = false,
  direction = 0, // For cones and lines (degrees)
  targetMode = 'center', // 'center' or 'corner' for grid targeting
  isPreview = false, // True when showing mouse hover preview
  casterToken = null // Token that is casting the AoE (for anchoring cones/lines)
}) => {

  // Calculate the affected grid cells and visual shapes
  const { affectedCells, visualShapes } = useMemo(() => {
    const normalizedAreaOfEffect = normalizeAreaOfEffect(areaOfEffect);

    if (!isVisible || !targetPoint || !normalizedAreaOfEffect) {
      return { affectedCells: [], visualShapes: [] };
    }

    const { shape, size } = normalizedAreaOfEffect;
    const radiusInCells = Math.ceil(size / feetPerCell);
    const radiusInPixels = radiusInCells * gridSize;

    // @date_of_change 2025-09-16 - TOKEN-ANCHORED AOE TARGETING
    // For cones and lines, use caster token position as origin point
    // For other shapes (circles, squares), use target point
    const isDirectionalAoE = shape.toLowerCase() === 'cone' || shape.toLowerCase() === 'line';
    let originPoint = targetPoint;
    let calculatedDirection = direction;
    let originPointPixelX, originPointPixelY; // Edge-based origin coordinates

    if (isDirectionalAoE && casterToken) {
      // @date_of_change 2025-09-17 - FIXED: Use token center for origin point calculation
      // Use caster token CENTER position as origin for directional AoE
      // First calculate token dimensions to get the proper center
      let tokenWidth = casterToken.width || gridSize;
      let tokenHeight = casterToken.height || gridSize;

      // If token has sizeCells, use that for accurate large creature dimensions
      if (casterToken.sizeCells && casterToken.sizeCells.w && casterToken.sizeCells.h) {
        tokenWidth = casterToken.sizeCells.w * gridSize;
        tokenHeight = casterToken.sizeCells.h * gridSize;
      }

      // Calculate grid origin from token center, not top-left corner
      const centerPixelX = casterToken.x + (tokenWidth / 2);
      const centerPixelY = casterToken.y + (tokenHeight / 2);

      originPoint = {
        x: Math.floor(centerPixelX / gridSize),
        y: Math.floor(centerPixelY / gridSize)
      };

      // Calculate direction from caster CENTER to target point (using calculated token dimensions)
      const casterCenterX = casterToken.x + (tokenWidth / 2);
      const casterCenterY = casterToken.y + (tokenHeight / 2);
      const targetCenterX = (targetPoint.x * gridSize) + (targetMode === 'center' ? gridSize / 2 : 0);
      const targetCenterY = (targetPoint.y * gridSize) + (targetMode === 'center' ? gridSize / 2 : 0);

      const deltaX = targetCenterX - casterCenterX;
      const deltaY = targetCenterY - casterCenterY;
      calculatedDirection = Math.atan2(deltaY, deltaX) * 180 / Math.PI;

      // @date_of_change 2025-09-17 - FIXED: Edge-based origin for large creatures
      // For D&D 5e accuracy, cone should originate from token edge facing the target
      const directionRad = calculatedDirection * Math.PI / 180;

      // For large creatures, calculate the appropriate edge distance
      const tokenRadiusX = tokenWidth / 2;
      const tokenRadiusY = tokenHeight / 2;

      // Calculate edge point on token perimeter in the direction of the target
      // Use proper ellipse equation for non-square creatures
      const absX = Math.abs(Math.cos(directionRad));
      const absY = Math.abs(Math.sin(directionRad));
      let edgeDistance;

      if (tokenRadiusX === tokenRadiusY) {
        // Circular/square token - simple radius calculation
        edgeDistance = tokenRadiusX;
      } else {
        // Rectangular token - ellipse edge calculation
        edgeDistance = (tokenRadiusX * tokenRadiusY) /
          Math.sqrt(tokenRadiusY * tokenRadiusY * absX * absX + tokenRadiusX * tokenRadiusX * absY * absY);
      }

      originPointPixelX = casterCenterX + Math.cos(directionRad) * edgeDistance;
      originPointPixelY = casterCenterY + Math.sin(directionRad) * edgeDistance;

      console.log(`[AoETargetingOverlay] 🎯 Token-anchored ${shape}:`, {
        casterPosition: { x: casterToken.x, y: casterToken.y },
        casterGrid: originPoint,
        targetGrid: targetPoint,
        calculatedDirection: calculatedDirection.toFixed(1) + '°'
      });
    }

    // @date_of_change 2025-09-16 - FIX: Proper center calculation for directional AoE
    // Convert origin point to pixel coordinates
    let centerX, centerY;

    if (isDirectionalAoE && casterToken) {
      // For directional AoE (cones/lines), use edge-based origin point if calculated
      if (typeof originPointPixelX !== 'undefined' && typeof originPointPixelY !== 'undefined') {
        centerX = originPointPixelX;
        centerY = originPointPixelY;
      } else {
        // Fallback to token center
        centerX = casterToken.x + (gridSize / 2);
        centerY = casterToken.y + (gridSize / 2);
      }
    } else {
      // For non-directional AoE (circles/squares), use target point
      const originX = originPoint.x * gridSize;
      const originY = originPoint.y * gridSize;

      centerX = originX;
      centerY = originY;

      if (targetMode === 'center') {
        // When targeting center of square, add half grid size
        centerX += gridSize / 2;
        centerY += gridSize / 2;
      }
      // For corner targeting, use the exact grid intersection
    }

    const shapes = [];
    const cells = [];

    // @date_of_change 2025-09-15 - Preview mode visual adjustments
    // Make preview mode more subtle with reduced opacity
    const baseOpacity = isPreview ? 0.5 : 1.0;
    const strokeOpacity = isPreview ? 0.6 : 0.8;

    switch (shape.toLowerCase()) {
      case 'sphere':
      case 'circle':
        // Create circle shape
        shapes.push({
          type: 'circle',
          x: centerX,
          y: centerY,
          radius: radiusInPixels,
          fill: `rgba(255, 100, 100, ${0.3 * baseOpacity})`,
          stroke: `rgba(255, 50, 50, ${strokeOpacity})`,
          strokeWidth: 2
        });

        // Calculate affected cells using RangeCalculator with target mode
        const circleAffected = RangeCalculator.calculateAreaOfEffect(
          targetPoint,
          normalizedAreaOfEffect,
          gridType,
          feetPerCell,
          targetMode
        );
        cells.push(...circleAffected);
        break;

      case 'cube':
      case 'square':
        // Create square shape
        const halfSize = radiusInPixels;
        shapes.push({
          type: 'rect',
          x: centerX - halfSize,
          y: centerY - halfSize,
          width: halfSize * 2,
          height: halfSize * 2,
          fill: 'rgba(100, 255, 100, 0.3)',
          stroke: 'rgba(50, 255, 50, 0.8)',
          strokeWidth: 2
        });

        // Calculate affected cells with target mode
        const squareAffected = RangeCalculator.calculateAreaOfEffect(
          targetPoint,
          normalizedAreaOfEffect,
          gridType,
          feetPerCell,
          targetMode
        );
        cells.push(...squareAffected);
        break;

      case 'cone':
        // @date_of_change 2025-09-16 - Use calculated direction for token-anchored cones
        // Create cone shape based on calculated direction
        const coneLength = radiusInPixels;
        const directionRad = calculatedDirection * Math.PI / 180;

        // Calculate cone points
        const point1X = centerX + Math.cos(directionRad - Math.PI/4) * coneLength;
        const point1Y = centerY + Math.sin(directionRad - Math.PI/4) * coneLength;
        const point2X = centerX + Math.cos(directionRad + Math.PI/4) * coneLength;
        const point2Y = centerY + Math.sin(directionRad + Math.PI/4) * coneLength;

        shapes.push({
          type: 'path',
          data: `M ${centerX} ${centerY} L ${point1X} ${point1Y} A ${coneLength} ${coneLength} 0 0 1 ${point2X} ${point2Y} Z`,
          fill: `rgba(255, 255, 100, ${0.3 * baseOpacity})`,
          stroke: `rgba(255, 255, 50, ${strokeOpacity})`,
          strokeWidth: 2
        });

        // @date_of_change 2025-10-15 - Use RangeCalculator cone template to match engine exactly
        if (isDirectionalAoE && casterToken) {
          const originData = RangeCalculator.getDirectionalAoEOrigin(casterToken, calculatedDirection, { gridSize });
          const coneAffectedCells = RangeCalculator.calculateConeAreaCells(
            originData.edge.grid,
            calculatedDirection,
            size,
            { feetPerCell }
          );
          cells.push(...coneAffectedCells);
        } else {
          const coneAffected = RangeCalculator.calculateAreaOfEffect(
            originPoint,
            normalizedAreaOfEffect,
            gridType,
            feetPerCell
          );
          cells.push(...coneAffected);
        }
        break;

      case 'line': {
        // @date_of_change 2025-10-11 - Lightning Bolt: project 100 ft line from caster edge per PHB 2024
        const lineLengthFeet = Number(size) || 0;
        const lineWidthFeet = Number(normalizedAreaOfEffect.width || normalizedAreaOfEffect.thickness || 5);
        const lengthInPixels = (lineLengthFeet / feetPerCell) * gridSize;
        const widthInPixels = Math.max(gridSize, (lineWidthFeet / feetPerCell) * gridSize);
        const directionRad = calculatedDirection * Math.PI / 180;

        let startPixelX = centerX;
        let startPixelY = centerY;
        let originGridCoords = originPoint;

        if (isDirectionalAoE && casterToken) {
          const originData = RangeCalculator.getDirectionalAoEOrigin(casterToken, calculatedDirection, { gridSize });
          startPixelX = originData.edge.pixel.x;
          startPixelY = originData.edge.pixel.y;
          originGridCoords = originData.edge.grid;
        }

        const endPixelX = startPixelX + Math.cos(directionRad) * lengthInPixels;
        const endPixelY = startPixelY + Math.sin(directionRad) * lengthInPixels;

        shapes.push({
          type: 'line',
          points: [startPixelX, startPixelY, endPixelX, endPixelY],
          stroke: `rgba(100, 100, 255, ${strokeOpacity})`,
          strokeWidth: widthInPixels,
          lineCap: 'butt'
        });

        const directionalLineCells = RangeCalculator.calculateLineAreaCells(
          originGridCoords,
          calculatedDirection,
          lineLengthFeet,
          lineWidthFeet,
          { feetPerCell }
        );
        cells.push(...directionalLineCells);
        break;
      }

      default:
        // Unknown shape: show small circle at target point
        shapes.push({
          type: 'circle',
          x: centerX,
          y: centerY,
          radius: gridSize / 4,
          fill: 'rgba(200, 200, 200, 0.5)',
          stroke: 'rgba(150, 150, 150, 0.8)',
          strokeWidth: 1
        });
        break;
    }

    const uniqueCells = Array.from(new Map(
      cells.map((cell) => [`${cell.x},${cell.y}`, cell])
    ).values());

    return { affectedCells: uniqueCells, visualShapes: shapes };
  }, [targetPoint, areaOfEffect, gridSize, feetPerCell, isVisible, direction, targetMode, casterToken, isPreview, gridType]);

  // Render grid highlights for affected cells
  const cellHighlights = useMemo(() => {
    const cellOpacity = isPreview ? 0.3 : 0.5;
    return affectedCells.map((cell, index) => (
      <Rect
        key={`cell-${index}`}
        x={cell.x * gridSize}
        y={cell.y * gridSize}
        width={gridSize}
        height={gridSize}
        fill={`rgba(255, 255, 255, ${0.2 * cellOpacity})`}
        stroke={`rgba(255, 255, 255, ${0.4 * cellOpacity})`}
        strokeWidth={1}
        listening={false}
      />
    ));
  }, [affectedCells, gridSize, isPreview]);

  // Render visual shapes
  const shapeElements = useMemo(() => {
    return visualShapes.map((shape, index) => {
      const key = `shape-${index}`;

      switch (shape.type) {
        case 'circle':
          return (
            <Circle
              key={key}
              x={shape.x}
              y={shape.y}
              radius={shape.radius}
              fill={shape.fill}
              stroke={shape.stroke}
              strokeWidth={shape.strokeWidth}
              listening={false}
            />
          );

        case 'rect':
          return (
            <Rect
              key={key}
              x={shape.x}
              y={shape.y}
              width={shape.width}
              height={shape.height}
              fill={shape.fill}
              stroke={shape.stroke}
              strokeWidth={shape.strokeWidth}
              listening={false}
            />
          );

        case 'line':
          return (
            <Line
              key={key}
              points={shape.points}
              stroke={shape.stroke}
              strokeWidth={shape.strokeWidth}
              lineCap={shape.lineCap}
              listening={false}
            />
          );

        case 'path':
          return (
            <Path
              key={key}
              data={shape.data}
              fill={shape.fill}
              stroke={shape.stroke}
              strokeWidth={shape.strokeWidth}
              listening={false}
            />
          );

        default:
          return null;
      }
    });
  }, [visualShapes]);

  // @date_of_change 2025-09-16 - FIX: Add proper safety checks and debugging
  // @date_of_change: 2025-10-04 - Removed spammy render logging

  if (!isVisible || !targetPoint || !areaOfEffect) {
    return null;
  }

  // @date_of_change 2025-09-16 - FIX: Ensure we have valid React elements before rendering
  const validCellHighlights = cellHighlights.filter(element => {
    // Check if element is a valid React element and not a text node or primitive
    if (element === null || element === undefined) return false;
    if (typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') return false;
    if (!element || typeof element !== 'object') return false;
    if (!element.type || typeof element.props !== 'object') return false;
    // Additional check: ensure it's a Konva component (has displayName or type.displayName)
    if (element.type && typeof element.type.displayName === 'string') {
      return element.type.displayName.includes('Rect') || element.type.displayName.includes('Shape');
    }
    return true;
  });

  const validShapeElements = shapeElements.filter(element => {
    // Check if element is a valid React element and not a text node or primitive
    if (element === null || element === undefined) return false;
    if (typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') return false;
    if (!element || typeof element !== 'object') return false;
    if (!element.type || typeof element.props !== 'object') return false;
    // Additional check: ensure it's a Konva component
    if (element.type && typeof element.type.displayName === 'string') {
      return element.type.displayName.includes('Circle') ||
             element.type.displayName.includes('Rect') ||
             element.type.displayName.includes('Line') ||
             element.type.displayName.includes('Path');
    }
    return true;
  });

  // @date_of_change 2025-09-16 - FIX REVERT: Use Group under the parent Layer
  return (
    <Group listening={false}>
      {/* Render cell highlights first (under the shape) */}
      {validCellHighlights}

      {/* Render the main AoE shape */}
      {validShapeElements}

      {/* Target point indicator - with safety check and targeting mode visual feedback */}
      {targetPoint && typeof targetPoint.x === 'number' && typeof targetPoint.y === 'number' && (
        <>
          {/* Primary target indicator */}
          <Circle
            key="target-point-indicator"
            x={targetPoint.x * gridSize + (targetMode === 'center' ? gridSize / 2 : 0)}
            y={targetPoint.y * gridSize + (targetMode === 'center' ? gridSize / 2 : 0)}
            radius={targetMode === 'intersection' ? 6 : 4}
            fill={targetMode === 'intersection' ? "orange" : "red"}
            stroke="white"
            strokeWidth={2}
            listening={false}
          />

          {/* Additional visual feedback for intersection targeting */}
          {targetMode === 'intersection' && (
            <>
              {/* Small cross indicator for intersection */}
              <Line
                key="intersection-cross-h"
                points={[
                  targetPoint.x * gridSize - 8, targetPoint.y * gridSize,
                  targetPoint.x * gridSize + 8, targetPoint.y * gridSize
                ]}
                stroke="orange"
                strokeWidth={2}
                listening={false}
              />
              <Line
                key="intersection-cross-v"
                points={[
                  targetPoint.x * gridSize, targetPoint.y * gridSize - 8,
                  targetPoint.x * gridSize, targetPoint.y * gridSize + 8
                ]}
                stroke="orange"
                strokeWidth={2}
                listening={false}
              />
            </>
          )}
        </>
      )}
    </Group>
  );
};

export default AoETargetingOverlay;
